import React, { useMemo, useState } from 'react';
import { resolveTextLocale } from '@/utils/scriptLocale';
import {
  applyWordBoundaryTransliteration,
  DRAVIDIAN_TRANSLITERATION_HINT,
  isDravidianTransliteration,
  transliterationSchemeForLanguage,
} from '@/utils/transliteration';

export interface LocalizedTextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * Short language code (e.g. 'ta', 'hi', 'ar', 'ur') when the field's
   * language is already known — sets text direction/script font
   * deterministically, and (for Indic languages) enables live Latin ->
   * native-script transliteration as the user types. Omit to auto-detect
   * direction/font from the live text instead (for free-text fields with
   * no fixed language, like a pasted transcript).
   */
  language?: string;
}

/**
 * Drop-in replacement for a plain `<textarea>` that gets direction (LTR/RTL)
 * and a script-appropriate font right for whatever language the content is
 * in, instead of silently assuming Latin/LTR — see utils/scriptLocale.ts.
 *
 * For Indic languages (Tamil, Hindi, Bengali, etc.) it also offers live
 * transliteration: type phonetically in Latin ("vanakkam") and it converts
 * to the native script (வணக்கம்) as soon as you finish the word, so editors
 * without a native keyboard/IME installed can still produce correct
 * native-script text. See utils/transliteration.ts for how the conversion
 * itself works and why only a just-completed word is ever rewritten.
 */
const LocalizedTextArea = React.forwardRef<HTMLTextAreaElement, LocalizedTextAreaProps>(
  ({ language, value, style, onChange, ...rest }, ref) => {
    const stringValue = typeof value === 'string' ? value : '';
    const locale = useMemo(() => resolveTextLocale(language, stringValue), [language, stringValue]);
    const scheme = useMemo(() => transliterationSchemeForLanguage(language), [language]);
    const [transliterateEnabled, setTransliterateEnabled] = useState(true);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (scheme && transliterateEnabled) {
        const result = applyWordBoundaryTransliteration(
          stringValue,
          e.target.value,
          e.target.selectionStart ?? e.target.value.length,
          scheme
        );
        if (result) {
          e.target.value = result.value;
          e.target.setSelectionRange(result.cursor, result.cursor);
        }
      }
      onChange?.(e);
    };

    return (
      <div>
        <textarea
          ref={ref}
          value={value}
          onChange={handleChange}
          dir={locale.dir}
          lang={language || undefined}
          style={{ fontFamily: locale.fontFamily, ...style }}
          {...rest}
        />
        {scheme && (
          <div className="mt-1">
            <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 select-none cursor-pointer">
              <input
                type="checkbox"
                className="h-3 w-3"
                checked={transliterateEnabled}
                onChange={(e) => setTransliterateEnabled(e.target.checked)}
              />
              Auto-convert typed English to {scheme.name} script
            </label>
            {transliterateEnabled && isDravidianTransliteration(language) && (
              <p className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">{DRAVIDIAN_TRANSLITERATION_HINT}</p>
            )}
          </div>
        )}
      </div>
    );
  }
);

LocalizedTextArea.displayName = 'LocalizedTextArea';

export default LocalizedTextArea;
